import React from 'react';
import PropTypes from 'prop-types';
import BusComponent from '../BusComponent';
import classNames from 'classnames';

import Key from '../../enums/Key';
import Popover from '../popover/Popover';

import HelpTourStep from './HelpTourStep';
import HelpTourWelcome from './HelpTourWelcome';
import {
    HelpTourTargets,
    HelpTourNewUserSteps,
    HelpTourWelcomeOptions,
    DEFAULT_ARROW_OFFSET,
    HelpTourExistingUserSteps,
    HelpTourVersions,
} from '../../enums/HelpTourDefinitions';
import { focusFirstChild, hasParentNode } from '../../helpers/Util';

/**
 * @typedef Props
 * @property {import('../../objects/MapInstance').default[]} mapInstances
 *
 * @typedef State
 * @property {boolean} open
 * @property {boolean} showTourWelcome
 * @property {Record<import('../../types').HelpTourStepId, import('../../types').HelpTourStep>} tourSteps
 * @property {import('../../types').HelpTourStep} currentStep
 * @property {boolean} stepLoading
 *
 * @extends {BusComponent<Props, State>}
 */

class HelpTour extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            open: false,
            showTourWelcome: false,
            tourSteps: HelpTourNewUserSteps,
            currentStep: Object.values(HelpTourNewUserSteps)[0],
            stepLoading: false,
        };

        this.focusBackElement = undefined;

        this.boundOnLockFocusInsideModal = this.onLockFocusInsideModal.bind(this);
    }

    componentDidMount() {
        this.bindGluBusEvents({
            HELP_TOUR_START_REQUEST: this.onHelpTourStartRequest,
            SET_ON_MODAL_CLOSE_FOCUS_ELEMENT: this.onSetOnModalCloseFocusElement,
            HELP_TOUR_STEP_LOADED: this.onHelpTourStepLoaded,
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const { open, currentStep } = this.state;

        const tourJustOpened = open && !prevState.open;
        const tourJustClosed = !open && prevState.open;

        if (tourJustOpened) {
            // the HelpTour is opened so we can attach the focus and key listener
            document.addEventListener('keydown', this.onKeydownCallback);
            document.addEventListener('focus', this.boundOnLockFocusInsideModal, true);

            this.bus.emit('COLLAPSE_ALL');
        }

        if (tourJustClosed) {
            // on close help tour remove the listeners
            document.removeEventListener('keydown', this.onKeydownCallback);
            document.removeEventListener('focus', this.boundOnLockFocusInsideModal, true);
        }

        // Toggle map layers panel based on step
        const prevStepId = prevState.currentStep.id;
        const currStepId = currentStep.id;

        if (prevStepId !== currStepId) {
            if (currStepId === 'map_layers') {
                this.setState({ stepLoading: true });
                this.bus.emit('TOGGLE_MAP_LAYERS_PANEL', { isVisible: true });
            } else if (prevStepId === 'map_layers') {
                this.bus.emit('TOGGLE_MAP_LAYERS_PANEL', { isVisible: false });
            }
        }
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
        this.onClose();
    }

    componentWillUpdate() {
        this.clearAssignedTargetClasses();
    }

    clearAssignedTargetClasses() {
        const tourTargetElements = Array.from(document.querySelectorAll('.help-tour-target'));
        tourTargetElements.forEach((element, i) =>
            tourTargetElements[i].classList.remove('help-tour-target'),
        );
    }    

    onKeydownCallback = event => {
        const { currentStep, showTourWelcome } = this.state;

        const forwardNavigationAvailable = currentStep.nextStepId && !showTourWelcome;
        const backwardsNavigationAvailable = currentStep.previousStepId && !showTourWelcome;

        const stepForwards = event.which === Key.RIGHT && forwardNavigationAvailable;
        const stepBackwards = event.which === Key.LEFT && backwardsNavigationAvailable;

        const tourConfirmation = event.which === Key.ENTER && showTourWelcome;
        const tourCancelation = event.which === Key.ESC;
        const tourCompletion = event.which === Key.ENTER && currentStep.nextStepId === undefined;

        if (stepForwards) {
            this.onNextStep();
        } else if (stepBackwards) {
            this.onPreviousStep();
        } else if (tourConfirmation) {
            this.onHelpTourStartTourRequest();
            event.stopPropagation();
            event.preventDefault();
        } else if (tourCancelation || tourCompletion) {
            this.onClose();
        }
    };

    onLockFocusInsideModal = event => {
        if (this._ignoreFocusChanges || hasParentNode(event.target, this.helpTour)) {
            return;
        }

        this._ignoreFocusChanges = true;
        focusFirstChild(this.helpTour);
        this._ignoreFocusChanges = false;
    };

    onClose = () => {
        const { tourSteps } = this.state;
        this.bus.emit('TOGGLE_MAP_LAYERS_PANEL', { isVisible: true });
        this.bus.emit('HELP_TOUR_STATUS_UPDATE_REQUEST', {
            isDone: true,
            tourVersion: HelpTourVersions.V2,
        });
        this.setState(
            {
                open: false,
                currentStep: Object.values(tourSteps)[0],
                showTourWelcome: false,
            },
            () => {
                if (this.focusBackElement) {
                    this.focusBackElement.focus();
                }
            },
        );
    };

    onPreviousStep = () => {
        const { currentStep, tourSteps } = this.state;
        if (currentStep.previousStepId) {
            const previousStep = tourSteps[currentStep.previousStepId];
            this.setState({ currentStep: previousStep, stepLoading: previousStep.waitForLoading });
        }
    };

    onNextStep = () => {
        const { currentStep, tourSteps } = this.state;
        if (currentStep.nextStepId) {
            const nextStep = tourSteps[currentStep.nextStepId];

            this.setState({ currentStep: nextStep, stepLoading: nextStep.waitForLoading });
        }
    };

    onHelpTourStepLoaded = () => {
        this.setState({ stepLoading: false });
    };

    onSelectedStep = stepId => {
        const { tourSteps } = this.state;
        this.setState({ currentStep: tourSteps[stepId] });
    };

    onHelpTourStartRequest = ({ tourSteps }) => {
        const currentTourSteps = tourSteps ? tourSteps : HelpTourNewUserSteps;
        const currentStep = Object.values(currentTourSteps)[0];

        this.setState({
            showTourWelcome: true,
            tourSteps: currentTourSteps,
            currentStep,
            open: true,
        });
    };

    onHelpTourStartTourRequest = () => {
        this.setState({
            showTourWelcome: false,
        });
    };

    onSetOnModalCloseFocusElement(element) {
        this.focusBackElement = element.trigger ? element.trigger : element;
    }

    showWelcome() {
        const { tourSteps } = this.state;

        return (
            <HelpTourWelcome
                onClose={this.onClose}
                onTourStart={this.onHelpTourStartTourRequest}
                helpTourRef={node => {
                    this.helpTour = node;
                }}
                existingUser={tourSteps === HelpTourExistingUserSteps}
            />
        );
    }

    showSteps() {
        const { currentStep, tourSteps } = this.state;
        return (
            <HelpTourStep
                options={currentStep}
                onClose={this.onClose}
                onPrevious={this.onPreviousStep}
                onNext={this.onNextStep}
                onSelected={this.onSelectedStep}
                helpTourRef={node => {
                    this.helpTour = node;
                }}
                tourSteps={tourSteps}
                bus={this.bus}
            />
        );
    }

    tourContent() {
        const { open, currentStep, showTourWelcome } = this.state;

        const { position, targetId, highlightId, verticalCentering } = showTourWelcome
            ? HelpTourWelcomeOptions
            : currentStep;

        const popoverContent = showTourWelcome ? this.showWelcome() : this.showSteps();

        const highlightElementId = highlightId || targetId;

        const anchorElement =
            document.querySelector(`[data-tourId='${targetId}']`) ||
            document.getElementById('root-container');
        const highlightElement = document.querySelector(`[data-tourId='${highlightElementId}']`);

        if (this.searchlight && highlightElement && !showTourWelcome) {
            // Cover whole map layers panel with spotlight
            requestAnimationFrame(() => {
                const highlightElementBox = highlightElement.getBoundingClientRect();

                this.searchlight.style.width = `${highlightElementBox.width}px`;
                this.searchlight.style.height = `${highlightElementBox.height}px`;
                this.searchlight.style.top = `${highlightElementBox.top}px`;
                this.searchlight.style.left = `${highlightElementBox.left}px`;
            });
        } else if (this.searchlight && showTourWelcome) {
            this.searchlight.style = '';
        }

        if (verticalCentering) {
            const anchorElementBox = anchorElement.getBoundingClientRect();
            const { anchorOrigin } = position;

            const ARROW_OFFSET =
                anchorOrigin.vertical === 'top' ? -DEFAULT_ARROW_OFFSET : DEFAULT_ARROW_OFFSET;
            position.marginTop = ARROW_OFFSET + anchorElementBox.height / 2;
        }

        if (highlightElement && !showTourWelcome) {
            highlightElement.classList.add('help-tour-target');
        }

        return (
            <Popover
                anchorEl={anchorElement}
                open={open}
                className="help-tour-popover"
                autoWidth
                {...position}
            >
                {popoverContent}
            </Popover>
        );
    }

    render() {
        const { open, showTourWelcome, stepLoading } = this.state;
        const classes = classNames('help-tour-backdrop', {
            'welcome-step': showTourWelcome,
            hidden: !open || stepLoading,
        });
        return (
            <div className={classes} data-tourId={HelpTourTargets.WELCOME} aria-hidden={!open}>
                <div
                    className="help-tour-searchlight"
                    ref={c => {
                        this.searchlight = c;
                    }}
                />
                {open && this.tourContent()}
            </div>
        );
    }
}

HelpTour.propTypes = {
    mapInstances: PropTypes.array.isRequired,
};

export default HelpTour;
